import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { JobProvider } from './contexts/JobContext';
import { ActivityFeedProvider } from './contexts/ActivityFeedContext';
import { UserFollowProvider } from './contexts/UserFollowContext';
import { ChatPopupProvider } from './contexts/ChatPopupContext';
import ChatPopupManager from './components/Chat/ChatPopupManager';
import ChatSidebar from './components/Chat/ChatSidebar';
import Header from './components/Header';
import Hero from './components/Hero';
import HowItWorks from './components/HowItWorks';
import Categories from './components/Categories';
import CategoriesPage from './components/CategoriesPage';
import AllCategories from './components/AllCategories';
import SearchResults from './components/SearchResults';
import Footer from './components/Footer';
import Auth from './components/Auth';
import UserDashboard from './components/UserDashboard';
import AccountSettings from './components/AccountSettings';
import ProfileStatusPage from './components/ProfileStatusPage';
import TokenExpirationHandler from './components/TokenExpirationHandler';
import EmailVerification from './components/EmailVerification';
import ResetPasswordPage from './components/ResetPasswordPage';
import PhoneTest from './components/PhoneTest';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';
import CookieConsent from './components/CookieConsent';
import PublicProfile from './components/PublicProfile';
import { ForumPage } from './components/Forum';
import BlogPage from './components/Blog/BlogPage';
import BlogDetailPage from './components/Blog/BlogDetailPage';
import MaintenanceMode from './components/MaintenanceMode';
import BotBlocked from './components/BotBlocked';
import BottomNavigationBar from './components/BottomNavigationBar';
import ProfileSidebar from './components/ProfileSidebar';
import ActivityFeed from './components/ActivityFeed/ActivityFeed';
import JobsPage from './components/Jobs/JobsPage';
import MyJobsPage from './components/Jobs/MyJobsPage';
import AppliedJobsPage from './components/Jobs/AppliedJobsPage';
import CompanySetupPage from './components/Company/CompanySetupPage';
import JobDetailPage from './components/Jobs/JobDetailPage';
import TalentSearchPage from './components/Talent/TalentSearchPage';
import NotificationsPage from './components/NotificationsPage';
import HowItWorksPage from './components/HowItWorksPage';
import ProtectedRoute from './components/ProtectedRoute';
import ChatLayout from './components/Chat/ChatLayout';
import { settingsAPI } from './services/api';
import { shouldBlockBot, getBotName, isBot, loadBotBlockingSettings, setBotBlockingSettings } from './utils/botDetection';
import { Helmet } from 'react-helmet';

// Home page component
function HomePage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isProfileSidebarOpen, setIsProfileSidebarOpen] = React.useState(false);

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleSignupClick = () => {
    navigate('/signup');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleProfileButtonClick = () => {
    setIsProfileSidebarOpen(true);
  };

  const handleAccountSettingsClick = () => {
    navigate('/settings');
  };

  const handleProfileStatusClick = () => {
    navigate('/profile/status');
  };

  const handleCategoriesClick = () => {
    navigate('/categories');
  };

  const handleHowItWorksClick = () => {
    // Scroll to how it works section on home page
    const howItWorksSection = document.getElementById('how-it-works');
    if (howItWorksSection) {
      howItWorksSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      // If not on home page, navigate there first
      navigate('/');
      setTimeout(() => {
        document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleForumClick = () => {
    navigate('/forum');
  };

  const handleJobsClick = () => {
    navigate('/vacancies');
  };

  const handleMessagesClick = () => {
    navigate('/chat');
  };

  const handleNotificationsClick = () => {
    // Handled by NotificationSystem component
  };

  // Show landing page for both logged-in and non-logged-in users
  // Feed functionality will be implemented later
  return (
    <div className="App home-page-with-bottom-nav">
      <Helmet>
        <title>Your Personal Career Copilot - Find Jobs, Connect with Talent, Advance Your Career</title>
        <meta name="description" content="Your Personal Career Copilot - Discover your next opportunity, connect with top talent, and advance your career. Find jobs, showcase your skills, and grow professionally." />
        <meta name="keywords" content="career, jobs, talent, professional growth, career opportunities, job search, talent discovery" />
        <meta property="og:title" content="Your Personal Career Copilot - Find Jobs, Connect with Talent, Advance Your Career" />
        <meta property="og:description" content="Your Personal Career Copilot - Discover your next opportunity, connect with top talent, and advance your career. Find jobs, showcase your skills, and grow professionally." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Your Personal Career Copilot" />
        <meta name="twitter:description" content="Your Personal Career Copilot - Discover your next opportunity, connect with top talent, and advance your career." />
        <meta name="twitter:site" content="@RemoteBirdy" />
      </Helmet>
      
      {/* Header - visible on desktop/tablet, hidden on mobile */}
      <div className="home-page-header">
        <Header 
          onLoginClick={handleLoginClick}
          onSignupClick={handleSignupClick}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={() => navigate('/')}
          onCategoriesClick={handleCategoriesClick}
          onHowItWorksClick={handleHowItWorksClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          onForumClick={handleForumClick}
          onJobsClick={handleJobsClick}
          currentPage="home"
          isHomePage={true}
        />
      </div>
      
      <Hero />
      <div id="how-it-works">
        <HowItWorks />
      </div>
      <div id="contact">
        <Footer />
      </div>
      
      {/* Bottom Navigation Bar */}
      <BottomNavigationBar
        onHomeClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
        onProfileClick={handleProfileButtonClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onJobsClick={handleJobsClick}
        onMessagesClick={handleMessagesClick}
        onNotificationsClick={handleNotificationsClick}
        onForumClick={handleForumClick}
      />
      
      {/* Profile Sidebar */}
      <ProfileSidebar
        isOpen={isProfileSidebarOpen}
        onClose={() => setIsProfileSidebarOpen(false)}
        onProfileClick={handleProfileClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onProfileStatusClick={handleProfileStatusClick}
      />
    </div>
  );
}

// Auth page component
function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleAuthSuccess = (userData, authType) => {
    // For both login and registration, redirect to dashboard
    navigate('/profile');
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleCategoriesClick = () => {
    navigate('/#categories');
  };

  const handleHowItWorksClick = () => {
    navigate('/#how-it-works');
  };

  // Determine if we should show login or signup based on the route
  const isLoginRoute = location.pathname === '/login';

  return (
    <div className="App">
      <Auth 
        onAuthSuccess={handleAuthSuccess} 
        onClose={() => navigate('/')} 
        onHomeClick={handleHomeClick}
        onCategoriesClick={handleCategoriesClick}
        onHowItWorksClick={handleHowItWorksClick}
        defaultMode={isLoginRoute ? 'login' : 'signup'}
      />
    </div>
  );
}

// Remote Industry Top Selection page component
function RemoteIndustryTopSelectionPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleSignupClick = () => {
    navigate('/signup');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleAccountSettingsClick = () => {
    navigate('/settings');
  };

  const handleCategoriesClick = () => {
    navigate('/categories');
  };

  const handleHowItWorksClick = () => {
    navigate('/#how-it-works');
  };

  const handleForumClick = () => {
    navigate('/forum');
  };

  const handleJobsClick = () => {
    navigate('/vacancies');
  };

  const handleMessagesClick = () => {
    navigate('/chat');
  };

  return (
    <div className="App remote-industry-top-selection-with-bottom-nav">
      <div className="remote-industry-top-selection-header">
        <Header 
          onLoginClick={handleLoginClick}
          onSignupClick={handleSignupClick}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={() => navigate('/')}
          onCategoriesClick={handleCategoriesClick}
          onHowItWorksClick={handleHowItWorksClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          onForumClick={handleForumClick}
          onJobsClick={handleJobsClick}
          currentPage="remote-industry-top-selection"
        />
      </div>
      <Categories 
        title="Remote Industry Top Selection"
        description="Explore the most in-demand categories and services in the remote work industry"
      />
      <Footer />
      
      {/* Bottom Navigation Bar - Visible only on mobile */}
      <BottomNavigationBar
        onHomeClick={() => navigate('/')}
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
        onProfileClick={handleProfileClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onJobsClick={handleJobsClick}
        onMessagesClick={handleMessagesClick}
        onForumClick={handleForumClick}
      />
    </div>
  );
}

// Profile page component
function ProfilePage() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          color: '#666'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleCategoriesClick = () => {
    navigate('/#categories');
  };

  const handleHowItWorksClick = () => {
    navigate('/#how-it-works');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleAccountSettingsClick = () => {
    navigate('/settings');
  };

  const handleProfileStatusClick = () => {
    navigate('/profile/status');
  };

  const handleForumClick = () => {
    navigate('/forum');
  };

  return (
    <div className="App">
      <UserDashboard 
        onClose={() => navigate('/')}
        onHomeClick={handleHomeClick}
        onCategoriesClick={handleCategoriesClick}
        onHowItWorksClick={handleHowItWorksClick}
        onProfileClick={handleProfileClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onForumClick={handleForumClick}
      />
    </div>
  );
}

// Account Settings page component
function AccountSettingsPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          color: '#666'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleCategoriesClick = () => {
    navigate('/#categories');
  };

  const handleHowItWorksClick = () => {
    navigate('/#how-it-works');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleForumClick = () => {
    navigate('/forum');
  };

  return (
    <div className="App">
      <AccountSettings 
        onClose={() => navigate('/profile')}
        onHomeClick={handleHomeClick}
        onCategoriesClick={handleCategoriesClick}
        onHowItWorksClick={handleHowItWorksClick}
        onProfileClick={handleProfileClick}
        onForumClick={handleForumClick}
      />
    </div>
  );
}

// Main app content component that uses auth context
function AppContent() {
  const [maintenanceMode, setMaintenanceMode] = useState(null);
  // Start with checking state - we need to check first before showing anything
  const [isCheckingMaintenance, setIsCheckingMaintenance] = useState(true);
  
  // Check if bot should be blocked on frontend
  const [isBotBlocked, setIsBotBlocked] = useState(null);

  // Check for bot on mount - block before any API calls
  useEffect(() => {
    // Load cached bot blocking settings first
    loadBotBlockingSettings().then(() => {
      // Check bot after settings are loaded (from cache or will be from API)
      if (shouldBlockBot()) {
        const botName = getBotName();
        setIsBotBlocked({
          blocked: true,
          botName: botName
        });
        
        // Add class to body to isolate styles
        document.body.classList.add('bot-blocked-active');
        document.documentElement.style.overflow = 'hidden';
        
        return () => {
          document.body.classList.remove('bot-blocked-active');
          document.documentElement.style.overflow = '';
        };
      } else {
        setIsBotBlocked({ blocked: false });
      }
    }).catch(() => {
      // If loading fails, use basic detection
      if (shouldBlockBot()) {
        const botName = getBotName();
        setIsBotBlocked({
          blocked: true,
          botName: botName
        });
        document.body.classList.add('bot-blocked-active');
        document.documentElement.style.overflow = 'hidden';
      } else {
        setIsBotBlocked({ blocked: false });
      }
    });
  }, []);

  const checkMaintenanceMode = async () => {
    // Don't make API call if bot is detected - already blocked
    if (isBotBlocked && isBotBlocked.blocked) {
      return;
    }

    try {
      // Add timeout to prevent long waits (max 2 seconds for faster response)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 2000)
      );
      
      const response = await Promise.race([
        settingsAPI.checkMaintenanceMode(),
        timeoutPromise
      ]);

      if (response.success && response.data) {
        // Update bot blocking settings from backend
        if (response.data.bot_blocking) {
          setBotBlockingSettings(response.data.bot_blocking);
        }

        if (response.data.enabled) {
          setMaintenanceMode({
            enabled: true,
            message: response.data.message,
            backDate: response.data.back_date
          });
        } else {
          setMaintenanceMode({ enabled: false });
        }
      } else {
        // If check fails, assume maintenance mode is off
        setMaintenanceMode({ enabled: false });
      }
    } catch (error) {
      console.error('Error checking maintenance mode:', error);
      // If check fails or times out, assume maintenance mode is off
      // This ensures the site loads even if API is slow
      setMaintenanceMode({ enabled: false });
    } finally {
      setIsCheckingMaintenance(false);
    }
  };

  useEffect(() => {
    // Only check maintenance mode if not a bot (bots are already blocked)
    // Wait for bot check to complete first
    if (isBotBlocked === null) {
      return; // Still checking for bots
    }
    
    if (!isBotBlocked.blocked) {
      checkMaintenanceMode();
    } else {
      // Bot is blocked, mark maintenance check as complete
      setIsCheckingMaintenance(false);
    }
  }, [isBotBlocked]); // Run after bot check completes

  const handleHomeClick = () => {
    window.location.href = '/';
  };

  const handleCategoriesClick = () => {
    window.location.href = '/categories';
  };

  const handleHowItWorksClick = () => {
    window.location.href = '/#how-it-works';
  };

  const handleHowItWorksPageClick = () => {
    window.location.href = '/how-it-works';
  };

  const handleProfileClick = () => {
    window.location.href = '/profile';
  };

  const handleAccountSettingsClick = () => {
    window.location.href = '/settings';
  };

  const handleForumClick = () => {
    window.location.href = '/forum';
  };

  const handleJobsClick = () => {
    window.location.href = '/vacancies';
  };

  const handleLoginClick = () => {
    window.location.href = '/login';
  };

  const handleSignupClick = () => {
    window.location.href = '/signup';
  };

  // Show bot blocked page immediately if bot is detected (prevents any API calls)
  if (isBotBlocked && isBotBlocked.blocked) {
    return <BotBlocked botName={isBotBlocked.botName} />;
  }

  // Show maintenance mode immediately if enabled (prevents flash of normal site)
  if (maintenanceMode && maintenanceMode.enabled) {
    return <MaintenanceMode message={maintenanceMode.message} backDate={maintenanceMode.backDate} />;
  }

  // Show loading screen while checking (prevents white screen flash)
  if (isCheckingMaintenance || isBotBlocked === null) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f8f9fa',
        zIndex: 9999
      }}>
        <div style={{
          textAlign: 'center'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #e1e5e9',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{
            color: '#666',
            fontSize: '16px',
            margin: 0
          }}>Loading...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <Router>
      <TokenExpirationHandler />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/remote-industry-top-selection" element={<RemoteIndustryTopSelectionPage />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/categories/all" element={<AllCategories />} />
        <Route path="/how-it-works" element={
          <HowItWorksPage 
            onHomeClick={handleHomeClick} 
            onCategoriesClick={handleCategoriesClick} 
            onProfileClick={handleProfileClick} 
            onAccountSettingsClick={handleAccountSettingsClick} 
            onForumClick={handleForumClick}
            onJobsClick={handleJobsClick}
            onLoginClick={handleLoginClick}
            onSignupClick={handleSignupClick}
          />
        } />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/signup" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage onHomeClick={handleHomeClick} onCategoriesClick={handleCategoriesClick} onHowItWorksClick={handleHowItWorksClick} />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/status" element={<ProfileStatusPage />} />
        <Route path="/company/setup" element={
          <ProtectedRoute>
            <CompanySetupPage />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={<AccountSettingsPage />} />
        <Route path="/forum" element={<ForumPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogDetailPage />} />
        <Route path="/vacancies" element={
          <ProtectedRoute>
            <JobsPage 
              onHomeClick={handleHomeClick} 
              onCategoriesClick={handleCategoriesClick} 
              onHowItWorksClick={handleHowItWorksClick} 
              onProfileClick={handleProfileClick} 
              onAccountSettingsClick={handleAccountSettingsClick} 
              onForumClick={handleForumClick}
            />
          </ProtectedRoute>
        } />
        <Route path="/vacancies/:id" element={
          <JobDetailPage 
            onHomeClick={handleHomeClick} 
            onCategoriesClick={handleCategoriesClick} 
            onHowItWorksClick={handleHowItWorksClick} 
            onProfileClick={handleProfileClick} 
            onAccountSettingsClick={handleAccountSettingsClick} 
            onForumClick={handleForumClick}
          />
        } />
        <Route path="/job/:token" element={
          <JobDetailPage 
            onHomeClick={handleHomeClick} 
            onCategoriesClick={handleCategoriesClick} 
            onHowItWorksClick={handleHowItWorksClick} 
            onProfileClick={handleProfileClick} 
            onAccountSettingsClick={handleAccountSettingsClick} 
            onForumClick={handleForumClick}
          />
        } />
        <Route path="/my-jobs" element={
          <ProtectedRoute>
            <MyJobsPage 
              onHomeClick={handleHomeClick} 
              onCategoriesClick={handleCategoriesClick} 
              onHowItWorksClick={handleHowItWorksClick} 
              onProfileClick={handleProfileClick} 
              onAccountSettingsClick={handleAccountSettingsClick} 
              onForumClick={handleForumClick} 
            />
          </ProtectedRoute>
        } />
        <Route path="/applied-jobs" element={
          <ProtectedRoute>
            <AppliedJobsPage 
              onHomeClick={handleHomeClick} 
              onCategoriesClick={handleCategoriesClick} 
              onHowItWorksClick={handleHowItWorksClick} 
              onProfileClick={handleProfileClick} 
              onAccountSettingsClick={handleAccountSettingsClick} 
              onForumClick={handleForumClick} 
            />
          </ProtectedRoute>
        } />
        <Route path="/notifications" element={
          <NotificationsPage 
            onHomeClick={handleHomeClick} 
            onCategoriesClick={handleCategoriesClick} 
            onHowItWorksClick={handleHowItWorksClick} 
            onProfileClick={handleProfileClick} 
            onAccountSettingsClick={handleAccountSettingsClick} 
            onForumClick={handleForumClick}
            onLoginClick={handleLoginClick}
            onSignupClick={handleSignupClick}
          />
        } />
        <Route path="/chat" element={
          <ProtectedRoute>
            <ChatLayout />
          </ProtectedRoute>
        } />
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="/phone-test" element={<PhoneTest />} />
        <Route path="/talent-search" element={
          <TalentSearchPage 
            onHomeClick={handleHomeClick} 
            onCategoriesClick={handleCategoriesClick} 
            onHowItWorksClick={handleHowItWorksClick} 
            onProfileClick={handleProfileClick} 
            onAccountSettingsClick={handleAccountSettingsClick} 
            onForumClick={handleForumClick}
            onJobsClick={handleJobsClick}
            onLoginClick={handleLoginClick}
            onSignupClick={handleSignupClick}
          />
        } />
        <Route path="/all-talents" element={
          <TalentSearchPage 
            onHomeClick={handleHomeClick} 
            onCategoriesClick={handleCategoriesClick} 
            onHowItWorksClick={handleHowItWorksClick} 
            onProfileClick={handleProfileClick} 
            onAccountSettingsClick={handleAccountSettingsClick} 
            onForumClick={handleForumClick}
            onJobsClick={handleJobsClick}
            onLoginClick={handleLoginClick}
            onSignupClick={handleSignupClick}
          />
        } />
        <Route path="/all-jobs" element={
          <ProtectedRoute>
            <JobsPage 
              onHomeClick={handleHomeClick} 
              onCategoriesClick={handleCategoriesClick} 
              onHowItWorksClick={handleHowItWorksClick} 
              onProfileClick={handleProfileClick} 
              onAccountSettingsClick={handleAccountSettingsClick} 
              onForumClick={handleForumClick}
            />
          </ProtectedRoute>
        } />
        <Route path="/talent/:username" element={<PublicProfile />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy onHomeClick={handleHomeClick} onCategoriesClick={handleCategoriesClick} onHowItWorksClick={handleHowItWorksClick} />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions onHomeClick={handleHomeClick} onCategoriesClick={handleCategoriesClick} onHowItWorksClick={handleHowItWorksClick} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CookieConsent />
      <ChatPopupManager />
      <ChatSidebar />
    </Router>
  );
}

// Main App component with AuthProvider
function App() {
  return (
    <AuthProvider>
      <CompanyProvider>
        <JobProvider>
          <ActivityFeedProvider>
            <UserFollowProvider>
              <ChatPopupProvider>
                <AppContent />
              </ChatPopupProvider>
            </UserFollowProvider>
          </ActivityFeedProvider>
        </JobProvider>
      </CompanyProvider>
    </AuthProvider>
  );
}

export default App;